import { useState } from "react";
import { Select, selectState as ss } from "@bfc-software/pallet";
import type { SelectMsg, SelectState } from "@bfc-software/pallet";
import { match } from "ts-pattern";
import { useCurrentLocalization } from "@bfc-software/bfc-web-ui/i18n";

import { useTaskFilters, customersParam } from "../-data/filters";
import {
  useCustomerCatalog,
  type CustomerOption,
} from "../-data/use-customer-catalog";

/**
 * The customer filter, shared by both tabs.
 *
 * It sits in the tab bar beside `ZoneFilter`, for the same reason: `customers`
 * is a URL param `useTaskFilters` owns, and a control that lived inside one tab
 * would stop narrowing the moment the other tab was open.
 *
 * The same *kind* of control as `ZoneFilter` - a value field opening a popover
 * with search and several picks - so the row teaches one way to fill in a
 * filter. It is pallet's `Select` in its `multiple` arm and nothing else.
 *
 * The value is `CustomerRec.id` (the spec's `customer_ext_id`), not `location_id`,
 * which is only the display "Customer ID". Options are labelled by `name` alone
 * and the typed text matches the name alone: nobody types a customer's id.
 *
 * Like `ZoneFilter`, this reads no `AuthContext`: `/customer-recs/list` is scoped
 * server-side by the token.
 *
 * As in `ZoneFilter`, selection is the URL's (`filters.customer`) and the typed
 * text is the only local state; pallet owns open/closed and this filter clears
 * its text on `closed`.
 */
/** Pure: everything the dropdown shows, from the catalog and the picks. */
function customerSelectState(a: {
  options: ReadonlyArray<CustomerOption>;
  isPending: boolean;
  selected: Array<CustomerOption>;
  text: string;
  t: (key: string) => string;
}): SelectState<CustomerOption> {
  const options = a.isPending
    ? ss.loading(a.t("tasks.customerFilter.loading"))
    : ss.ready({
        items: a.options.filter(ss.byLabel(a.text)),
        emptyLabel: a.t("tasks.customerFilter.empty"),
      });
  return ss.withPicks(
    ss.withSearch(
      ss.multiple({
        label: ss.ariaLabel(a.t("tasks.customerFilter.ariaLabel")),
        placeholder: a.t("tasks.customerFilter.placeholder"),
        options,
      }),
      {
        placeholder: a.t("tasks.customerFilter.searchPlaceholder"),
        text: a.text,
      },
    ),
    ss.subsetSelection(a.selected),
  );
}

export function CustomerFilter() {
  const [t] = useCurrentLocalization();
  const { filters, setSearch } = useTaskFilters();
  const catalog = useCustomerCatalog();
  const options = catalog.data ?? [];

  /** The only local state: the search box. The picks live in the URL. */
  const [text, setText] = useState("");

  /**
   * Off the ADT rather than off the catalog: a link carrying an ext id the
   * catalog does not have (a deleted customer, a paste from another warehouse)
   * still names itself instead of vanishing from the field.
   */
  const selected: CustomerOption[] =
    filters.customer.kind === "any"
      ? []
      : filters.customer.extIds.map(
          (extId) =>
            options.find((option) => option.id === extId) ?? {
              id: extId,
              label: extId,
            },
        );

  const commit = (extIds: string[]) =>
    setSearch({ customers: customersParam({ kind: "some", extIds }) });

  // `all` resolves against the FULL catalog, not the searched-down list.
  const idsOf = (selection: ss.MultipleSelection<CustomerOption>) =>
    match(selection)
      .with({ kind: "all" }, () => options.map((option) => option.id))
      .with({ kind: "subset" }, (s) => s.items.map((option) => option.id))
      .with({ kind: "empty" }, () => [])
      .exhaustive();

  const onMsg = (msg: SelectMsg<CustomerOption>) =>
    match(msg)
      .with({ kind: "searched" }, ({ text }) => setText(text))
      // Reset the search box, so reopening shows the full list.
      .with({ kind: "closed" }, () => setText(""))
      .with({ kind: "pickedN" }, ({ selection }) => commit(idsOf(selection)))
      .with({ kind: "cleared" }, () => commit([]))
      // `picked` cannot arrive from `multiple`
      .with({ kind: "picked" }, { kind: "opened" }, () => {})
      .exhaustive();

  return (
    <Select
      state={customerSelectState({
        options,
        isPending: catalog.isPending,
        selected,
        text,
        t,
      })}
      onMsg={onMsg}
      className="flex-1 min-w-0 ml-16"
      data-testid="customer-filter"
    />
  );
}
